import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layout/MainLayout';
import { Toaster } from 'react-hot-toast';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ComingSoon from './pages/ComingSoon';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import MyOrders from './pages/MyOrders';
import ProductDetails from './pages/ProductDetails';
import PaymentCallback from './pages/PaymentCallback';
import OrderDetailsPage from './pages/OrderDetailsPage';
import PaymentsPage from './pages/PaymentsPage';
import ShipmentPage from './pages/ShipmentPage';
import WishlistPage from './pages/WishlistPage';
import ProductListingPage from './pages/ProductListingPage';
import UserProfilePage from './pages/UserProfilePage';
import HelpCenter from './pages/support/HelpCenter';
import ContactUs from './pages/support/ContactUs';
import AboutUs from './pages/support/AboutUs';
import FAQ from './pages/support/FAQ';
import { PrivacyPolicy, TermsOfService } from './pages/support/LegalPages';
import CustomOrderPage from './pages/CustomOrderPage';

// Placeholder Pages
const Clothing = () => <div><h1 className="text-3xl font-bold">Clothing</h1></div>;

// Layout Wrapper
const LayoutWrapper = ({ children }) => {
  return <MainLayout>{children}</MainLayout>;
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen w-full flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
};

function App() {
  const isComingSoon = process.env.REACT_APP_COMING_SOON === 'true';

  // When Coming Soon mode is enabled, strictly intercept all routes (*)
  // so no other pages or features can be accessed via URL
  if (isComingSoon) {
    return (
      <Router>
        <LanguageProvider>
          <Routes>
            <Route path="*" element={<ComingSoon />} />
          </Routes>
        </LanguageProvider>
      </Router>
    );
  }

  const toastConfig = (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: 'rgba(27, 13, 11, 0.9)',
          color: '#fff',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '16px 24px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '400',
          letterSpacing: '0.02em',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
          maxWidth: '400px',
        },
        success: {
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
          style: {
            borderLeft: '4px solid #10b981',
          },
        },
        error: {
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fff',
          },
          style: {
            borderLeft: '4px solid #ef4444',
          },
        },
      }}
    />
  );

  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            {toastConfig}
            <Routes>
            {/* Standalone Coming Soon Route (Previewable Anytime) */}
            <Route path="/coming-soon" element={<ComingSoon />} />

            {/* Auth Routes (No Layout) */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Public Main App Routes */}
            <Route path="/" element={<LayoutWrapper><Home /></LayoutWrapper>} />
            <Route path="/search" element={<LayoutWrapper><ProductListingPage /></LayoutWrapper>} />
            <Route path="/category/:slug" element={<LayoutWrapper><ProductListingPage /></LayoutWrapper>} />
            <Route path="/product/:id" element={<LayoutWrapper><ProductDetails /></LayoutWrapper>} />
            <Route path="/clothing" element={<LayoutWrapper><Clothing /></LayoutWrapper>} />

            {/* Protected Main App Routes */}
            <Route path="/cart" element={<ProtectedRoute><LayoutWrapper><CartPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/wishlist" element={<ProtectedRoute><LayoutWrapper><WishlistPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><LayoutWrapper><CheckoutPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/payment/callback" element={<ProtectedRoute><LayoutWrapper><PaymentCallback /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><LayoutWrapper><MyOrders /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/orders/:id" element={<ProtectedRoute><LayoutWrapper><OrderDetailsPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><LayoutWrapper><PaymentsPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/shipment" element={<ProtectedRoute><LayoutWrapper><ShipmentPage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><LayoutWrapper><UserProfilePage /></LayoutWrapper></ProtectedRoute>} />
            <Route path="/custom-order" element={<ProtectedRoute><LayoutWrapper><CustomOrderPage /></LayoutWrapper></ProtectedRoute>} />

            {/* Support Routes */}
            <Route path="/help" element={<LayoutWrapper><HelpCenter /></LayoutWrapper>} />
            <Route path="/contact" element={<LayoutWrapper><ContactUs /></LayoutWrapper>} />
            <Route path="/about" element={<LayoutWrapper><AboutUs /></LayoutWrapper>} />
            <Route path="/faq" element={<LayoutWrapper><FAQ /></LayoutWrapper>} />
            <Route path="/privacy" element={<LayoutWrapper><PrivacyPolicy /></LayoutWrapper>} />
            <Route path="/terms" element={<LayoutWrapper><TermsOfService /></LayoutWrapper>} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  </Router>
  );
}

export default App;
